import { useState, type FormEvent } from "react";
import { createUser, type CreateUserPayload } from "../api/users";
import { ApiError } from "../api/client";
import { ALL_ROLES, ROLE_LABELS, type AcademyRole } from "../types/user";

interface Props {
  onClose: () => void;
  onCreated: (password: string, login: string) => void;
  allowedRoles: AcademyRole[];
}

export function CreateUserDialog({ onClose, onCreated, allowedRoles }: Props) {
  const [login, setLogin] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [telegramId, setTelegramId] = useState("");
  const [role, setRole] = useState<AcademyRole>(allowedRoles[0] ?? "trainee");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const payload: CreateUserPayload = {
      login: login.trim().toLowerCase(),
      full_name: fullName.trim(),
      role,
    };
    if (phone.trim()) payload.phone = phone.trim();
    if (telegramId.trim()) {
      const tid = Number(telegramId.trim());
      if (!Number.isFinite(tid) || tid <= 0) {
        setError("Telegram ID должен быть положительным числом");
        return;
      }
      payload.telegram_user_id = tid;
    }

    setSubmitting(true);
    try {
      const result = await createUser(payload);
      onCreated(result.generated_password, result.user.login);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "LOGIN_TAKEN") setError("Такой логин уже занят");
        else if (err.status === 403) setError("Нет прав для этой роли");
        else if (err.status === 422) setError("Проверьте поля формы");
        else setError("Не удалось создать пользователя");
      } else {
        setError("Сеть недоступна");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md space-y-4 rounded-lg bg-white p-6 shadow-lg"
      >
        <header className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Новый пользователь</h2>
            <p className="text-sm text-slate-500">
              Пароль будет сгенерирован автоматически и показан один раз.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Закрыть"
          >
            ×
          </button>
        </header>

        <div className="space-y-1">
          <label htmlFor="login" className="block text-sm font-medium text-slate-700">
            Логин (a-z, цифры, точки, тире)
          </label>
          <input
            id="login"
            type="text"
            required
            pattern="[a-z0-9._\-]+"
            minLength={2}
            maxLength={64}
            value={login}
            onChange={(e) => setLogin(e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="full_name" className="block text-sm font-medium text-slate-700">
            ФИО
          </label>
          <input
            id="full_name"
            type="text"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="phone" className="block text-sm font-medium text-slate-700">
              Телефон
            </label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="tg" className="block text-sm font-medium text-slate-700">
              Telegram ID
            </label>
            <input
              id="tg"
              type="text"
              inputMode="numeric"
              value={telegramId}
              onChange={(e) => setTelegramId(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
            />
          </div>
        </div>

        <div className="space-y-1">
          <label htmlFor="role" className="block text-sm font-medium text-slate-700">
            Роль
          </label>
          <select
            id="role"
            value={role}
            onChange={(e) => setRole(e.target.value as AcademyRole)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
          >
            {allowedRoles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
          {allowedRoles.length < ALL_ROLES.length && (
            <p className="text-xs text-slate-500">
              Доступны только разрешённые для вашей роли значения.
            </p>
          )}
        </div>

        {error && (
          <div role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary"
          >
            {submitting ? "Создаём…" : "Создать"}
          </button>
        </div>
      </form>
    </div>
  );
}
